import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  Building2,
  CreditCard,
  Database,
  FileText,
  Globe,
  Inbox,
  LayoutTemplate,
  Mail,
  MapPin,
  MousePointerClick,
  Search,
  ShieldCheck,
  Users,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getSmartAnalyticsCompanyActivities,
  getSmartAnalyticsCompanyDataService,
  getSmartAnalyticsCompanyFeedbacks,
  getSmartAnalyticsCompanyInvitations,
  getSmartAnalyticsCompanyJobTemplates,
  getSmartAnalyticsCompanyOverview,
  getSmartAnalyticsCompanyPayments,
  getSmartAnalyticsCompanyRecruiters,
  getSmartAnalyticsCompanySearches,
} from "../../Services/SmartAnalyticsService";
import {
  SmartAnalyticsActiveService,
  SmartAnalyticsActivityRow,
  SmartAnalyticsCompanyOverview,
  SmartAnalyticsDataServiceRow,
  SmartAnalyticsFeedbackRow,
  SmartAnalyticsInvitationRow,
  SmartAnalyticsJobTemplateRow,
  SmartAnalyticsPaymentRow,
  SmartAnalyticsRecruiterRow,
  SmartAnalyticsSearchRow,
  FEEDBACK_STATUS_OPTIONS,
  INVITATION_STATUS_OPTIONS,
} from "../../Types/SmartAnalyticsType";
import Pagination from "../../../Shared/Pagination/Pagination";
import { PaginationResponse } from "../../../Shared/Pagination/PaginationResponse";
import { formatDateTime } from "../../../Shared/Utils/HelperUtils";
import {
  EmptyState,
  fieldClass,
  headerClass,
  LoadingRows,
  MiniStat,
  PAGE_SIZE,
  SectionCard,
  statusBadge,
} from "./SmartAnalyticsShared";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(value);

const TABS = [
  { key: "OVERVIEW", label: "Overview", icon: ShieldCheck },
  { key: "RECRUITERS", label: "Recruiters", icon: Users },
  { key: "PAYMENTS", label: "Payments", icon: CreditCard },
  { key: "JOB_TEMPLATES", label: "Job Templates", icon: LayoutTemplate },
  { key: "ACTIVITY", label: "Activity", icon: MousePointerClick },
  { key: "SEARCHES", label: "Searches", icon: Search },
  { key: "INVITATIONS", label: "Invitations", icon: Inbox },
  { key: "DATA_SERVICE", label: "Data Service", icon: Database },
  { key: "FEEDBACK", label: "Feedback", icon: Mail },
] as const;

type CompanyTab = (typeof TABS)[number]["key"];

const usePaginatedLoad = <T,>(
  loadFn: (page: number, size: number) => Promise<PaginationResponse<T> | null>,
  deps: any[]
) => {
  const [data, setData] = useState<PaginationResponse<T> | null>(null);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setPage(0);
  }, deps);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadFn(page, PAGE_SIZE)
      .then((result) => {
        if (!cancelled && result) setData(result);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, loadFn, ...deps]);

  return { data, page, setPage, loading };
};

const SmartAnalyticsCompanyComponent = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<CompanyTab>("OVERVIEW");
  const [overview, setOverview] = useState<SmartAnalyticsCompanyOverview | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(true);

  const id = companyId!;

  const loadOverview = useCallback(async () => {
    if (!id) return;
    const response = await getSmartAnalyticsCompanyOverview(id);
    if (response.success) {
      setOverview(response.data);
    }
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    setOverviewLoading(true);
    loadOverview().finally(() => {
      if (!cancelled) setOverviewLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [loadOverview]);

  const recruiterFilterOptions = useRecruiterOptions(id);

  if (overviewLoading) {
    return (
      <div className="space-y-6">
        <div className="h-40 animate-pulse rounded-3xl bg-slate-900/40" />
        <div className="h-96 animate-pulse rounded-3xl bg-slate-900/40" />
      </div>
    );
  }

  if (!overview) {
    return (
      <div className="rounded-3xl border border-slate-800 bg-slate-900/40 p-12 text-center">
        <p className="text-sm font-black text-slate-200">Company not found</p>
        <button
          type="button"
          onClick={() => navigate("/admin/smart-analytics")}
          className="mt-4 rounded-xl bg-cyan-500 px-5 py-2.5 text-xs font-black uppercase tracking-wider text-slate-950"
        >
          Back to Smart Analytics
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <button
        type="button"
        onClick={() => navigate("/admin/smart-analytics")}
        className="flex items-center gap-1.5 text-xs font-black uppercase tracking-wider text-slate-400 transition hover:text-cyan-400"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Smart Analytics
      </button>

      <CompanyHeader overview={overview} />
      <OverviewStats overview={overview} />

      <div className="flex flex-wrap gap-2">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 rounded-xl px-3 py-2 text-[10px] font-black uppercase tracking-wider transition ${
                active
                  ? "border border-cyan-500/40 bg-cyan-500/15 text-cyan-300"
                  : "border border-slate-800 bg-slate-950/60 text-slate-500 hover:border-slate-700 hover:text-slate-300"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === "OVERVIEW" && <OverviewDetail overview={overview} />}
      {activeTab === "RECRUITERS" && <RecruitersSection companyId={id} />}
      {activeTab === "PAYMENTS" && <PaymentsSection companyId={id} />}
      {activeTab === "JOB_TEMPLATES" && (
        <JobTemplatesSection companyId={id} recruiterOptions={recruiterFilterOptions} />
      )}
      {activeTab === "ACTIVITY" && (
        <ActivitySection companyId={id} recruiterOptions={recruiterFilterOptions} />
      )}
      {activeTab === "SEARCHES" && (
        <SearchesSection companyId={id} recruiterOptions={recruiterFilterOptions} />
      )}
      {activeTab === "INVITATIONS" && (
        <InvitationsSection companyId={id} recruiterOptions={recruiterFilterOptions} />
      )}
      {activeTab === "DATA_SERVICE" && (
        <DataServiceSection companyId={id} recruiterOptions={recruiterFilterOptions} />
      )}
      {activeTab === "FEEDBACK" && (
        <FeedbackSection companyId={id} recruiterOptions={recruiterFilterOptions} />
      )}
    </div>
  );
};

const CompanyHeader = ({ overview }: { overview: SmartAnalyticsCompanyOverview }) => (
  <div className="rounded-3xl border border-slate-800 bg-slate-900/40 p-6">
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="flex items-start gap-4">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-slate-800 bg-slate-950/60 text-cyan-400">
          <Building2 className="h-7 w-7" />
        </div>
        <div>
          <h1 className="text-lg font-black text-slate-100">{overview.companyName}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-3 text-xs font-semibold text-slate-500">
            {overview.companyDomain && <span>{overview.companyDomain}</span>}
            {overview.industry && <span>{overview.industry}</span>}
            {overview.companySizeRange && <span>{overview.companySizeRange}</span>}
            {overview.headquartersLocation && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {overview.headquartersLocation}
              </span>
            )}
            {overview.website && (
              <a
                href={overview.website}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1 text-cyan-400 hover:underline"
              >
                <Globe className="h-3 w-3" />
                Website
              </a>
            )}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-[10px] font-semibold text-slate-500">
            <span className="rounded-lg border border-slate-800 bg-slate-950/60 px-2 py-1">
              Admin: {overview.adminName || "-"}
            </span>
            <span className="rounded-lg border border-slate-800 bg-slate-950/60 px-2 py-1">
              Joined {formatDateTime(overview.createdAt ?? undefined)}
            </span>
            <span className="rounded-lg border border-slate-800 bg-slate-950/60 px-2 py-1">
              Credits: {overview.totalCredits ?? 0}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const ActiveServicesSection = ({ services }: { services: SmartAnalyticsActiveService[] }) => {
  const active = services.filter((s) => s.active);
  const expired = services.filter((s) => !s.active && s.expired);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">
          Active plans / services
        </p>
        <span className="text-[10px] font-semibold text-slate-500">
          {active.length} active · {services.length} total
        </span>
      </div>

      {services.length === 0 ? (
        <div className="rounded-3xl border border-slate-800 bg-slate-900/40 p-5">
          <p className="text-sm font-black text-slate-200">No active services</p>
          <p className="text-xs font-semibold text-slate-500">
            This organization has no active trial, subscription, add-on or data-service plan.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {services.map((service, index) => (
            <ActiveServiceCard key={`${service.serviceType}-${service.paymentId ?? index}`} service={service} />
          ))}
        </div>
      )}
    </div>
  );
};

const ActiveServiceCard = ({ service }: { service: SmartAnalyticsActiveService }) => {
  const typeLabel: Record<string, string> = {
    TRIAL: "Trial free",
    SUBSCRIPTION: "Subscription",
    ADDON: "Add-on",
    DATA_SERVICE: "Data service",
  };

  const tone = service.active ? "emerald" : service.expired ? "rose" : "slate";
  const borderClass =
    tone === "emerald"
      ? "border-emerald-500/20"
      : tone === "rose"
      ? "border-rose-500/20"
      : "border-slate-700";

  return (
    <div className={`rounded-2xl border bg-slate-900/40 p-4 ${borderClass}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">
            {typeLabel[service.serviceType] || service.serviceType}
          </p>
          <p className="mt-1 text-sm font-black text-slate-100">{service.planName || "-"}</p>
        </div>
        <span
          className={`rounded-full px-2 py-0.5 text-[9px] font-black uppercase tracking-wider ${
            service.active
              ? "bg-emerald-500/10 text-emerald-400"
              : service.expired
              ? "bg-rose-500/10 text-rose-400"
              : "bg-slate-800 text-slate-500"
          }`}
        >
          {service.active ? "Active" : service.expired ? "Expired" : "Inactive"}
        </span>
      </div>

      <div className="mt-3 space-y-1 text-[10px] font-semibold text-slate-500">
        {service.amount != null && service.amount > 0 && (
          <p>
            Amount: <span className="text-slate-300">{formatCurrency(service.amount)}</span>
          </p>
        )}
        {service.creditsPurchased != null && service.creditsPurchased > 0 && (
          <p>
            Credits: <span className="text-slate-300">{service.creditsPurchased}</span>
          </p>
        )}
        {service.expireDate ? (
          <p>
            Expires: <span className="text-slate-300">{formatDateTime(service.expireDate)}</span>
            {service.daysUntilExpiry !== null && (
              <span className="ml-1 text-slate-500">({service.daysUntilExpiry} days)</span>
            )}
          </p>
        ) : (
          <p>No expiry</p>
        )}
        {service.purchasedAt && (
          <p>
            Purchased: <span className="text-slate-300">{formatDateTime(service.purchasedAt)}</span>
          </p>
        )}
      </div>
    </div>
  );
};

const OverviewStats = ({ overview }: { overview: SmartAnalyticsCompanyOverview }) => (
  <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
    <MiniStat
      label="Recruiters"
      value={overview.activeRecruiterCount}
      sub={`${overview.recruiterCount} total`}
      icon={<Users className="h-4 w-4" />}
      tone="cyan"
    />
    <MiniStat
      label="Job templates"
      value={overview.activeJobTemplateCount}
      sub={`${overview.jobTemplateCount} total`}
      icon={<LayoutTemplate className="h-4 w-4" />}
      tone="emerald"
    />
    <MiniStat
      label="Invitations"
      value={overview.invitationCount}
      sub={`${overview.pendingInvitationCount} pending · ${overview.acceptedInvitationCount} accepted`}
      icon={<Inbox className="h-4 w-4" />}
    />
    <MiniStat
      label="Revenue"
      value={formatCurrency(overview.totalRevenue)}
      sub={`${overview.paymentCount} payments · ${overview.totalCreditsPurchased} credits`}
      icon={<CreditCard className="h-4 w-4" />}
      tone="amber"
    />
    <MiniStat
      label="Activity"
      value={overview.activityCount}
      sub="Company-wide events"
      icon={<MousePointerClick className="h-4 w-4" />}
    />
    <MiniStat
      label="Saved searches"
      value={overview.savedSearchCount}
      sub="Filter sets created"
      icon={<Search className="h-4 w-4" />}
    />
    <MiniStat
      label="Data queries"
      value={overview.dataServiceQueryCount}
      sub="Console / builder runs"
      icon={<Database className="h-4 w-4" />}
    />
    <MiniStat
      label="Feedback"
      value={overview.openFeedbackCount}
      sub={`${overview.feedbackCount} total`}
      icon={<Mail className="h-4 w-4" />}
      tone="rose"
    />
  </div>
);

const OverviewDetail = ({ overview }: { overview: SmartAnalyticsCompanyOverview }) => (
  <SectionCard
    title={
      <h2 className="text-sm font-black text-slate-100">Company full dashboard</h2>
    }
  >
    <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 lg:grid-cols-3">
      <StatPair label="Total recruiters" value={overview.recruiterCount} />
      <StatPair label="Active recruiters" value={overview.activeRecruiterCount} />
      <StatPair label="Company admin" value={overview.adminName || "-"} />
      <StatPair label="Job templates" value={overview.jobTemplateCount} />
      <StatPair label="Active job templates" value={overview.activeJobTemplateCount} />
      <StatPair label="Total invitations" value={overview.invitationCount} />
      <StatPair label="Pending invitations" value={overview.pendingInvitationCount} />
      <StatPair label="Accepted invitations" value={overview.acceptedInvitationCount} />
      <StatPair label="Rejected invitations" value={overview.rejectedInvitationCount} />
      <StatPair label="Payments" value={overview.paymentCount} />
      <StatPair label="Total revenue" value={formatCurrency(overview.totalRevenue)} />
      <StatPair label="Credits purchased" value={overview.totalCreditsPurchased} />
      <StatPair label="Activity events" value={overview.activityCount} />
      <StatPair label="Saved searches" value={overview.savedSearchCount} />
      <StatPair label="Data service queries" value={overview.dataServiceQueryCount} />
      <StatPair label="Feedback" value={overview.feedbackCount} />
      <StatPair label="Open feedback" value={overview.openFeedbackCount} />
      <StatPair
        label="Last activity"
        value={formatDateTime(overview.lastActivityAt ?? undefined)}
      />
      <StatPair label="Company credits" value={overview.totalCredits ?? 0} />
    </div>
  </SectionCard>
);

const StatPair = ({ label, value }: { label: string; value: string | number }) => (
  <div className="rounded-2xl border border-slate-800 bg-slate-950/60 px-4 py-3">
    <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">{label}</p>
    <p className="mt-1 text-sm font-black text-slate-200">
      {typeof value === "number" ? value.toLocaleString() : value}
    </p>
  </div>
);

const RecruitersSection = ({ companyId }: { companyId: string }) => {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("");

  const load = useCallback(
    async (page: number, size: number) => {
      const response = await getSmartAnalyticsCompanyRecruiters(companyId, search, filter, page, size);
      return response.success ? response.data : null;
    },
    [companyId, search, filter]
  );

  const { data, page, setPage, loading } = usePaginatedLoad<SmartAnalyticsRecruiterRow>(load, [
    companyId,
    search,
    filter,
  ]);

  const applySearch = (value: string) => {
    setSearch(value);
    setPage(0);
  };

  return (
    <SectionCard
      title={
        <div>
          <h2 className="flex items-center gap-2 text-sm font-black text-slate-100">
            <Users className="h-4 w-4 text-cyan-400" />
            Recruiters
          </h2>
          <p className="mt-1 text-[10px] font-semibold text-slate-500">
            {data?.totalElements ?? 0} recruiters in this company
          </p>
        </div>
      }
      action={
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-600" />
            <input
              value={search}
              onChange={(event) => applySearch(event.target.value)}
              placeholder="Search recruiters"
              className={`${fieldClass} w-56 pl-9`}
            />
          </div>
          <select
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            className={`${fieldClass} w-40`}
          >
            <option value="">All</option>
            <option value="ADMIN">Admin</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </select>
        </div>
      }
    >
      {loading ? (
        <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-32 animate-pulse rounded-2xl bg-slate-800/50" />
          ))}
        </div>
      ) : !data || data.content.length === 0 ? (
        <EmptyState icon={<Users className="h-6 w-6" />} title="No recruiters" body="This company has no matching recruiters." />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 lg:grid-cols-3">
            {data.content.map((recruiter) => (
              <div
                key={recruiter.userId}
                className="rounded-2xl border border-slate-800 bg-slate-950/60 p-4 transition hover:border-slate-700"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-black text-slate-100">
                      {recruiter.fullName || recruiter.email}
                    </p>
                    <p className="text-[10px] font-semibold text-slate-500">{recruiter.email}</p>
                  </div>
                  {recruiter.companyAdmin ? (
                    <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[9px] font-black uppercase text-amber-400">
                      Admin
                    </span>
                  ) : null}
                </div>
                <div className="mt-3 flex flex-wrap gap-2 text-[10px] font-semibold text-slate-400">
                  {recruiter.designation && (
                    <span className="rounded-lg bg-slate-900/60 px-2 py-1">{recruiter.designation}</span>
                  )}
                  <span className="rounded-lg bg-slate-900/60 px-2 py-1">
                    {recruiter.recruiterActive ? "Active" : "Inactive"}
                  </span>
                  <span className="rounded-lg bg-slate-900/60 px-2 py-1">
                    {recruiter.invitationCount} invites
                  </span>
                  <span className="rounded-lg bg-slate-900/60 px-2 py-1">
                    {recruiter.jobTemplateCount} jobs
                  </span>
                </div>
                {recruiter.lastActivityAt && (
                  <p className="mt-3 text-[10px] font-semibold text-slate-600">
                    Last activity {formatDateTime(recruiter.lastActivityAt)}
                  </p>
                )}
              </div>
            ))}
          </div>
          {data.totalPages > 1 && <Pagination {...data} setPage={setPage} />}
        </>
      )}
    </SectionCard>
  );
};

const PaymentsSection = ({ companyId }: { companyId: string }) => {
  const [activeServices, setActiveServices] = useState<SmartAnalyticsActiveService[]>([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [planType, setPlanType] = useState("");

  const load = useCallback(
    async (page: number, size: number) => {
      const response = await getSmartAnalyticsCompanyPayments(
        companyId,
        search,
        status,
        planType,
        page,
        size
      );
      if (response.success) {
        setActiveServices(response.data.activeServices);
        return response.data.payments;
      }
      return null;
    },
    [companyId, search, status, planType]
  );

  const { data, page, setPage, loading } = usePaginatedLoad<SmartAnalyticsPaymentRow>(load, [
    companyId,
    search,
    status,
    planType,
  ]);

  const statusOptions = [
    { key: "", label: "All statuses" },
    { key: "SUCCESS", label: "Success" },
    { key: "PAID", label: "Paid" },
    { key: "CREATED", label: "Created" },
    { key: "FAILED", label: "Failed" },
    { key: "CANCELLED", label: "Cancelled" },
  ];

  const planTypeOptions = [
    { key: "", label: "All types" },
    { key: "TRIAL", label: "Trial" },
    { key: "SUBSCRIPTION", label: "Subscription" },
    { key: "ADDON", label: "Add-on" },
    { key: "DATA_SERVICE", label: "Data Service" },
  ];

  const applySearch = (value: string) => {
    setSearch(value);
    setPage(0);
  };

  return (
    <SectionCard
      title={
        <div>
          <h2 className="flex items-center gap-2 text-sm font-black text-slate-100">
            <CreditCard className="h-4 w-4 text-cyan-400" />
            Payments
          </h2>
          <p className="mt-1 text-[10px] font-semibold text-slate-500">
            {data?.totalElements ?? 0} payment records
          </p>
        </div>
      }
    >
      {activeServices.length > 0 && (
        <div className="border-b border-slate-800 p-6">
          <ActiveServicesSection services={activeServices} />
        </div>
      )}

      <div className="border-b border-slate-800 p-6">
        <div className="flex flex-col gap-4">
          <div className="relative max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-600" />
            <input
              value={search}
              onChange={(event) => applySearch(event.target.value)}
              placeholder="Search plan, recruiter, invoice, gateway"
              className={`${fieldClass} w-full pl-9`}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-black uppercase tracking-wider text-slate-500">Status</span>
            {statusOptions.map((option) => {
              const active = status === option.key;
              return (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => {
                    setStatus(option.key);
                    setPage(0);
                  }}
                  className={`rounded-xl px-3 py-1.5 text-[10px] font-black uppercase tracking-wider transition ${
                    active
                      ? "border border-cyan-500/40 bg-cyan-500/15 text-cyan-300"
                      : "border border-slate-800 bg-slate-950/60 text-slate-500 hover:border-slate-700 hover:text-slate-300"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-black uppercase tracking-wider text-slate-500">Type</span>
            {planTypeOptions.map((option) => {
              const active = planType === option.key;
              return (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => {
                    setPlanType(option.key);
                    setPage(0);
                  }}
                  className={`rounded-xl px-3 py-1.5 text-[10px] font-black uppercase tracking-wider transition ${
                    active
                      ? "border border-cyan-500/40 bg-cyan-500/15 text-cyan-300"
                      : "border border-slate-800 bg-slate-950/60 text-slate-500 hover:border-slate-700 hover:text-slate-300"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <table className="w-full">
        <thead className="border-y border-slate-800 bg-slate-900/60">
          <tr>
            <th className={`${headerClass} text-left`}>Plan</th>
            <th className={`${headerClass} text-left`}>Amount</th>
            <th className={`${headerClass} text-left`}>Credits</th>
            <th className={`${headerClass} text-left`}>Status</th>
            <th className={`${headerClass} text-left`}>Gateway</th>
            <th className={`${headerClass} text-left`}>Paid by</th>
            <th className={`${headerClass} text-left`}>Date</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <LoadingRows rows={4} cols={7} />
          ) : !data || data.content.length === 0 ? (
            <tr>
              <td colSpan={7}>
                <EmptyState
                  icon={<CreditCard className="h-6 w-6" />}
                  title="No payments"
                  body="No payment records for this company yet."
                />
              </td>
            </tr>
          ) : (
            data.content.map((payment) => (
              <tr key={payment.paymentId} className="border-t border-slate-800/70">
                <td className="px-5 py-4">
                  <p className="text-xs font-bold text-slate-200">{payment.planName || "-"}</p>
                  <div className="flex gap-1 text-[10px] font-semibold text-slate-500">
                    {payment.addon ? <span>addon</span> : null}
                    {payment.dataService ? <span>data-service</span> : null}
                  </div>
                </td>
                <td className="px-5 py-4 text-xs font-black text-emerald-400">
                  {payment.amount != null ? formatCurrency(payment.amount) : "-"}
                </td>
                <td className="px-5 py-4 text-xs font-semibold text-slate-300">
                  {payment.creditsPurchased ?? "-"}
                </td>
                <td className="px-5 py-4">{statusBadge(payment.paymentStatus)}</td>
                <td className="px-5 py-4 text-xs font-semibold text-slate-400">
                  {payment.gateway || "-"}
                </td>
                <td className="px-5 py-4">
                  <p className="text-xs font-semibold text-slate-200">{payment.recruiterName || "-"}</p>
                </td>
                <td className="px-5 py-4 text-xs font-semibold text-slate-500">
                  {formatDateTime(payment.createdAt ?? undefined)}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      {data && data.totalPages > 1 && <Pagination {...data} setPage={setPage} />}
    </SectionCard>
  );
};

const JobTemplatesSection = ({
  companyId,
  recruiterOptions,
}: {
  companyId: string;
  recruiterOptions: { userId: string; label: string }[];
}) => {
  const [search, setSearch] = useState("");
  const [recruiterId, setRecruiterId] = useState("");
  const [jobStatus, setJobStatus] = useState("");

  const load = useCallback(
    async (page: number, size: number) => {
      const response = await getSmartAnalyticsCompanyJobTemplates(
        companyId,
        search,
        recruiterId,
        jobStatus,
        page,
        size
      );
      return response.success ? response.data : null;
    },
    [companyId, search, recruiterId, jobStatus]
  );

  const { data, page, setPage, loading } = usePaginatedLoad<SmartAnalyticsJobTemplateRow>(load, [
    companyId,
    search,
    recruiterId,
    jobStatus,
  ]);

  return (
    <PaginatedTableSection
      title="Job Templates"
      icon={<LayoutTemplate className="h-4 w-4 text-cyan-400" />}
      loading={loading}
      data={data}
      page={page}
      setPage={setPage}
      search={search}
      onSearch={setSearch}
      searchPlaceholder="Search title, posted by"
      filters={
        <>
          <RecruiterFilter value={recruiterId} onChange={setRecruiterId} options={recruiterOptions} />
          <select value={jobStatus} onChange={(e) => setJobStatus(e.target.value)} className={`${fieldClass} w-36`}>
            <option value="">All statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </select>
        </>
      }
      columns={["Title", "Role level", "Work type", "Experience", "Compensation", "Posted by", "Created"]}
      renderRow={(job) => (
        <tr key={job.jobTemplateId} className="border-t border-slate-800/70">
          <td className="px-5 py-4">
            <p className="text-xs font-bold text-slate-200">{job.title || "-"}</p>
            {job.active ? (
              <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[9px] font-black uppercase text-emerald-400">
                active
              </span>
            ) : (
              <span className="rounded-full bg-slate-800 px-2 py-0.5 text-[9px] font-black uppercase text-slate-500">
                inactive
              </span>
            )}
          </td>
          <td className="px-5 py-4 text-xs font-semibold text-slate-400">{job.roleLevel || "-"}</td>
          <td className="px-5 py-4 text-xs font-semibold text-slate-400">{job.workType || "-"}</td>
          <td className="px-5 py-4 text-xs font-semibold text-slate-400">
            {job.minExperience || "-"} - {job.maxExperience || "-"}
          </td>
          <td className="px-5 py-4 text-xs font-black text-emerald-400">
            {job.compensationOffer != null ? formatCurrency(Number(job.compensationOffer)) : "-"}
          </td>
          <td className="px-5 py-4 text-xs font-semibold text-slate-400">{job.postedBy || "-"}</td>
          <td className="px-5 py-4 text-xs font-semibold text-slate-500">
            {formatDateTime(job.createdAt ?? undefined)}
          </td>
        </tr>
      )}
    />
  );
};

const ActivitySection = ({
  companyId,
  recruiterOptions,
}: {
  companyId: string;
  recruiterOptions: { userId: string; label: string }[];
}) => {
  const [search, setSearch] = useState("");
  const [recruiterId, setRecruiterId] = useState("");

  const load = useCallback(
    async (page: number, size: number) => {
      const response = await getSmartAnalyticsCompanyActivities(companyId, search, recruiterId, page, size);
      return response.success ? response.data : null;
    },
    [companyId, search, recruiterId]
  );

  const { data, page, setPage, loading } = usePaginatedLoad<SmartAnalyticsActivityRow>(load, [
    companyId,
    search,
    recruiterId,
  ]);

  return (
    <PaginatedTableSection
      title="Activity"
      icon={<MousePointerClick className="h-4 w-4 text-cyan-400" />}
      loading={loading}
      data={data}
      page={page}
      setPage={setPage}
      search={search}
      onSearch={setSearch}
      searchPlaceholder="Search message, event type"
      filters={<RecruiterFilter value={recruiterId} onChange={setRecruiterId} options={recruiterOptions} />}
      columns={["Event", "Message", "Actor", "Entity", "Candidate", "When"]}
      renderRow={(activity) => (
        <tr key={activity.activityId} className="border-t border-slate-800/70">
          <td className="px-5 py-4">
            <p className="text-xs font-bold text-slate-200">{activity.eventType || "-"}</p>
            <p className="text-[10px] font-semibold text-slate-500">{activity.category || "-"}</p>
          </td>
          <td className="px-5 py-4">
            <p className="max-w-xs text-xs font-semibold text-slate-300 line-clamp-2">{activity.message || "-"}</p>
          </td>
          <td className="px-5 py-4">
            <p className="text-xs font-semibold text-slate-200">{activity.actorName || "-"}</p>
            <p className="text-[10px] font-semibold text-slate-500">{activity.actorRole || "-"}</p>
          </td>
          <td className="px-5 py-4 text-xs font-semibold text-slate-400">{activity.entityType || "-"}</td>
          <td className="px-5 py-4 text-xs font-semibold text-slate-400">{activity.candidateUserId || "-"}</td>
          <td className="px-5 py-4 text-xs font-semibold text-slate-500">
            {formatDateTime(activity.createdAt ?? undefined)}
          </td>
        </tr>
      )}
    />
  );
};

const SearchesSection = ({
  companyId,
  recruiterOptions,
}: {
  companyId: string;
  recruiterOptions: { userId: string; label: string }[];
}) => {
  const [search, setSearch] = useState("");
  const [recruiterId, setRecruiterId] = useState("");

  const load = useCallback(
    async (page: number, size: number) => {
      const response = await getSmartAnalyticsCompanySearches(companyId, search, recruiterId, page, size);
      return response.success ? response.data : null;
    },
    [companyId, search, recruiterId]
  );

  const { data, page, setPage, loading } = usePaginatedLoad<SmartAnalyticsSearchRow>(load, [
    companyId,
    search,
    recruiterId,
  ]);

  return (
    <PaginatedTableSection
      title="Saved Searches"
      icon={<Search className="h-4 w-4 text-cyan-400" />}
      loading={loading}
      data={data}
      page={page}
      setPage={setPage}
      search={search}
      onSearch={setSearch}
      searchPlaceholder="Search saved filter name"
      filters={<RecruiterFilter value={recruiterId} onChange={setRecruiterId} options={recruiterOptions} />}
      columns={["Name", "Recruiter", "Alert", "Created", "Updated"]}
      renderRow={(searchRow) => (
        <tr key={searchRow.searchId} className="border-t border-slate-800/70">
          <td className="px-5 py-4 text-xs font-bold text-slate-200">{searchRow.name}</td>
          <td className="px-5 py-4">
            <p className="text-xs font-semibold text-slate-200">{searchRow.recruiterName || "-"}</p>
            <p className="text-[10px] font-semibold text-slate-500">{searchRow.recruiterEmail || "-"}</p>
          </td>
          <td className="px-5 py-4">
            {searchRow.alertEnabled ? (
              <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[9px] font-black uppercase text-emerald-400">
                {searchRow.alertFrequency || "On"}
              </span>
            ) : (
              <span className="rounded-full bg-slate-800 px-2 py-0.5 text-[9px] font-black uppercase text-slate-500">
                Off
              </span>
            )}
          </td>
          <td className="px-5 py-4 text-xs font-semibold text-slate-500">
            {formatDateTime(searchRow.createdAt ?? undefined)}
          </td>
          <td className="px-5 py-4 text-xs font-semibold text-slate-500">
            {formatDateTime(searchRow.updatedAt ?? undefined)}
          </td>
        </tr>
      )}
    />
  );
};

const InvitationsSection = ({
  companyId,
  recruiterOptions,
}: {
  companyId: string;
  recruiterOptions: { userId: string; label: string }[];
}) => {
  const [search, setSearch] = useState("");
  const [recruiterId, setRecruiterId] = useState("");
  const [status, setStatus] = useState("");

  const load = useCallback(
    async (page: number, size: number) => {
      const response = await getSmartAnalyticsCompanyInvitations(
        companyId,
        search,
        recruiterId,
        status,
        page,
        size
      );
      return response.success ? response.data : null;
    },
    [companyId, search, recruiterId, status]
  );

  const { data, page, setPage, loading } = usePaginatedLoad<SmartAnalyticsInvitationRow>(load, [
    companyId,
    search,
    recruiterId,
    status,
  ]);

  return (
    <PaginatedTableSection
      title="Invitations"
      icon={<Inbox className="h-4 w-4 text-cyan-400" />}
      loading={loading}
      data={data}
      page={page}
      setPage={setPage}
      search={search}
      onSearch={setSearch}
      searchPlaceholder="Search candidate, job"
      filters={
        <>
          <RecruiterFilter value={recruiterId} onChange={setRecruiterId} options={recruiterOptions} />
          <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${fieldClass} w-36`}>
            <option value="">All statuses</option>
            {INVITATION_STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s.toLowerCase()}
              </option>
            ))}
          </select>
        </>
      }
      columns={["Candidate", "Job", "Recruiter", "Status", "Invited", "Responded"]}
      renderRow={(invite) => (
        <tr key={invite.invitationId} className="border-t border-slate-800/70">
          <td className="px-5 py-4">
            <p className="text-xs font-bold text-slate-200">{invite.candidateName || "-"}</p>
            <p className="text-[10px] font-semibold text-slate-500">{invite.candidateEmail || "-"}</p>
          </td>
          <td className="px-5 py-4 text-xs font-semibold text-slate-300">{invite.jobTitle || "-"}</td>
          <td className="px-5 py-4 text-xs font-semibold text-slate-400">{invite.recruiterName || "-"}</td>
          <td className="px-5 py-4">{statusBadge(invite.status)}</td>
          <td className="px-5 py-4 text-xs font-semibold text-slate-500">
            {formatDateTime(invite.invitedAt ?? undefined)}
          </td>
          <td className="px-5 py-4 text-xs font-semibold text-slate-500">
            {formatDateTime(invite.respondedAt ?? undefined)}
          </td>
        </tr>
      )}
    />
  );
};

const DataServiceSection = ({
  companyId,
  recruiterOptions,
}: {
  companyId: string;
  recruiterOptions: { userId: string; label: string }[];
}) => {
  const [recruiterId, setRecruiterId] = useState("");

  const load = useCallback(
    async (page: number, size: number) => {
      const response = await getSmartAnalyticsCompanyDataService(companyId, recruiterId, page, size);
      return response.success ? response.data : null;
    },
    [companyId, recruiterId]
  );

  const { data, page, setPage, loading } = usePaginatedLoad<SmartAnalyticsDataServiceRow>(load, [
    companyId,
    recruiterId,
  ]);

  return (
    <PaginatedTableSection
      title="Data Service Activity"
      icon={<Database className="h-4 w-4 text-cyan-400" />}
      loading={loading}
      data={data}
      page={page}
      setPage={setPage}
      filters={<RecruiterFilter value={recruiterId} onChange={setRecruiterId} options={recruiterOptions} />}
      columns={["Recruiter", "Origin", "Table", "Rows", "Duration", "SQL", "When"]}
      renderRow={(row) => (
        <tr key={row.historyId} className="border-t border-slate-800/70">
          <td className="px-5 py-4">
            <p className="text-xs font-semibold text-slate-200">{row.recruiterName || "-"}</p>
            <p className="text-[10px] font-semibold text-slate-500">{row.recruiterEmail || "-"}</p>
          </td>
          <td className="px-5 py-4 text-xs font-semibold text-slate-400">{row.origin || "-"}</td>
          <td className="px-5 py-4 text-xs font-semibold text-slate-400">{row.tableName || "-"}</td>
          <td className="px-5 py-4 text-xs font-semibold text-slate-300">{row.rowCount ?? "-"}</td>
          <td className="px-5 py-4 text-xs font-semibold text-slate-400">{row.durationMs ?? "-"} ms</td>
          <td className="px-5 py-4">
            <p className="max-w-xs text-[10px] font-mono text-slate-500 line-clamp-2">{row.executedSql || "-"}</p>
            {row.success ? (
              <span className="text-[9px] font-black uppercase text-emerald-400">success</span>
            ) : (
              <span className="text-[9px] font-black uppercase text-rose-400">failed</span>
            )}
          </td>
          <td className="px-5 py-4 text-xs font-semibold text-slate-500">
            {formatDateTime(row.createdAt ?? undefined)}
          </td>
        </tr>
      )}
    />
  );
};

const FeedbackSection = ({
  companyId,
  recruiterOptions,
}: {
  companyId: string;
  recruiterOptions: { userId: string; label: string }[];
}) => {
  const [search, setSearch] = useState("");
  const [recruiterId, setRecruiterId] = useState("");
  const [status, setStatus] = useState("");

  const load = useCallback(
    async (page: number, size: number) => {
      const response = await getSmartAnalyticsCompanyFeedbacks(
        companyId,
        search,
        recruiterId,
        status,
        page,
        size
      );
      return response.success ? response.data : null;
    },
    [companyId, search, recruiterId, status]
  );

  const { data, page, setPage, loading } = usePaginatedLoad<SmartAnalyticsFeedbackRow>(load, [
    companyId,
    search,
    recruiterId,
    status,
  ]);

  return (
    <PaginatedTableSection
      title="Feedback"
      icon={<Mail className="h-4 w-4 text-cyan-400" />}
      loading={loading}
      data={data}
      page={page}
      setPage={setPage}
      search={search}
      onSearch={setSearch}
      searchPlaceholder="Search subject, message"
      filters={
        <>
          <RecruiterFilter value={recruiterId} onChange={setRecruiterId} options={recruiterOptions} />
          <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${fieldClass} w-40`}>
            <option value="">All statuses</option>
            {FEEDBACK_STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s.replace(/_/g, " ").toLowerCase()}
              </option>
            ))}
          </select>
        </>
      }
      columns={["Subject", "Recruiter", "Category", "Priority", "Status", "Created"]}
      renderRow={(fb) => (
        <tr key={fb.feedbackId} className="border-t border-slate-800/70">
          <td className="px-5 py-4">
            <p className="max-w-xs text-xs font-bold text-slate-200">{fb.subject || "-"}</p>
            <p className="max-w-xs text-[10px] font-semibold text-slate-500 line-clamp-1">{fb.message || "-"}</p>
          </td>
          <td className="px-5 py-4">
            <p className="text-xs font-semibold text-slate-200">{fb.recruiterName || "-"}</p>
            <p className="text-[10px] font-semibold text-slate-500">{fb.email || "-"}</p>
          </td>
          <td className="px-5 py-4 text-xs font-semibold text-slate-400">
            {fb.category?.replace(/_/g, " ").toLowerCase() || "-"}
          </td>
          <td className="px-5 py-4 text-xs font-semibold text-slate-400">
            {fb.priority?.toLowerCase() || "-"}
          </td>
          <td className="px-5 py-4">{statusBadge(fb.status)}</td>
          <td className="px-5 py-4 text-xs font-semibold text-slate-500">
            {formatDateTime(fb.createdAt ?? undefined)}
          </td>
        </tr>
      )}
    />
  );
};

const PaginatedTableSection = <T,>({
  title,
  icon,
  loading,
  data,
  page,
  setPage,
  search,
  onSearch,
  searchPlaceholder,
  filters,
  columns,
  renderRow,
}: {
  title: string;
  icon: React.ReactNode;
  loading: boolean;
  data: PaginationResponse<T> | null;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  search?: string;
  onSearch?: (value: string) => void;
  searchPlaceholder?: string;
  filters?: React.ReactNode;
  columns: string[];
  renderRow: (item: T) => React.ReactNode;
}) => {
  return (
    <SectionCard
      title={
        <div>
          <h2 className="flex items-center gap-2 text-sm font-black text-slate-100">
            {icon}
            {title}
          </h2>
          <p className="mt-1 text-[10px] font-semibold text-slate-500">
            {data?.totalElements ?? 0} records
          </p>
        </div>
      }
      action={
        <div className="flex flex-wrap items-center gap-2">
          {onSearch && (
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-600" />
              <input
                value={search}
                onChange={(event) => onSearch(event.target.value)}
                placeholder={searchPlaceholder || "Search"}
                className={`${fieldClass} w-56 pl-9`}
              />
            </div>
          )}
          {filters}
        </div>
      }
    >
      <table className="w-full">
        <thead className="border-y border-slate-800 bg-slate-900/60">
          <tr>
            {columns.map((col) => (
              <th
                key={col}
                className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-500"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <LoadingRows rows={4} cols={columns.length} />
          ) : !data || data.content.length === 0 ? (
            <tr>
              <td colSpan={columns.length}>
                <EmptyState icon={<FileText className="h-6 w-6" />} title="No records" body="Nothing matches the current filters." />
              </td>
            </tr>
          ) : (
            data.content.map((item) => renderRow(item))
          )}
        </tbody>
      </table>
      {data && data.totalPages > 1 && <Pagination {...data} setPage={setPage} />}
    </SectionCard>
  );
};

const RecruiterFilter = ({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { userId: string; label: string }[];
}) => (
  <select value={value} onChange={(event) => onChange(event.target.value)} className={`${fieldClass} w-48`}>
    <option value="">All recruiters</option>
    {options.map((opt) => (
      <option key={opt.userId} value={opt.userId}>
        {opt.label}
      </option>
    ))}
  </select>
);

const useRecruiterOptions = (companyId: string) => {
  const [options, setOptions] = useState<{ userId: string; label: string }[]>([]);

  useEffect(() => {
    let cancelled = false;
    getSmartAnalyticsCompanyRecruiters(companyId, "", "", 0, 200).then((response) => {
      if (cancelled || !response.success) return;
      setOptions(
        response.data.content.map((r) => ({
          userId: r.userId,
          label: r.fullName ? `${r.fullName} (${r.email || r.userId.slice(0, 8)})` : r.email || r.userId,
        }))
      );
    });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  return options;
};

export default SmartAnalyticsCompanyComponent;
